// BillingClaim.tsx

import {
  faBoltLightning,
  faChain,
  faCheck,
  faCircleInfo,
  faCircleQuestion,
  faExternalLink,
  faFile,
  faFileMedical,
  faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import * as timeago from 'timeago.js';

import ClaimLookup from '@/components/ClaimLookup';
import Debug from '@/components/Debug';
import NoteList from '@/components/NoteList';
import PageHeader from '@/components/PageHeader';
import InsuranceSummary from '@/components/PersonInsurancePolicy/InsuranceSummary';
import RemitServiceLineCard from '@/components/RemitServiceLineCard';
import SimpleFormat from '@/components/SimpleFormat/SimpleFormat';
import TaskList from '@/components/TaskList';
import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import Cms1500Repository from '@/repositories/Cms1500Repository';
import RemitClaimRepository from '@/repositories/RemitClaimRepository';
import {
  ClaimStatusRegistry,
  ModifierRegistry,
  RemitClaim,
  RemitServiceLine
} from '@/types/billing';
import { BillingItemMinimal, Cms1500Cptcode } from '@/types/encounter';
import Alert from '@/ui/components/Alert';
import Badge from '@/ui/components/Badge';
import Card from '@/ui/components/Card';
import Container from '@/ui/components/Container';
import CopyContent from '@/ui/components/CopyContent';
import DataList from '@/ui/components/DataList';
import DataRow from '@/ui/components/DataList/DataRow';
import DualTime from '@/ui/components/DualTime';
import Heading from '@/ui/components/Heading';
import Tooltip from '@/ui/components/Tooltip';
import ViewInCandid from '@/ui/components/ViewInCandid';
import { CandidLogo } from '@/ui/components/ViewInCandid/CandidLogo';
import { candidClaimUrl } from '@/utils/candidLink';
import { currencyInCentsFormatter } from '@/utils/currencies';
import encounterTypeFromMapping from '@/utils/encounterTypeFromMapping';
import { healthieLink } from '@/utils/healthieLinks';
import { titleize } from '@/utils/titleize';

import { computeClaimStatus } from './computeClaimStatus';

/** The `:by` discriminators the claim route accepts; anything else is a typo. */
const SUPPORTED_LOOKUPS = ['by_external_id', 'by_healthie_id', 'by_candid_encounter_id'];

const BillingClaim: React.FC = () => {
  const { id, by } = useParams<{ id: string; by: string }>();
  const { navigateToEncounterWithoutPerson } = useNavigateToPage();
  const { host } = useContext(AppContext);
  const cms1500Repo = Cms1500Repository();
  const remitRepo = RemitClaimRepository();

  const claimQueryResponse = useAPI({
    // `by` is part of the key because the same id string means a different
    // claim in each identifier space.
    queryKey: ['getHealthieCms1500Id', by, id],
    queryFn: async () => {
      if (by === 'by_external_id') {
        return await cms1500Repo.getCms1500ByExternalId({
          externalId: id || ''
        });
      } else if (by === 'by_healthie_id') {
        return await cms1500Repo.getCms1500ByHealthieId({
          cms1500ID: id || ''
        });
      } else if (by === 'by_candid_encounter_id') {
        return await cms1500Repo.getCms1500ByCandidEncounterId({
          candidEncounterId: id || ''
        });
      } else {
        return await cms1500Repo.getCms1500ById({
          cms1500ID: id || ''
        });
      }
    }
  });

  const { data, error } = claimQueryResponse;

  const remitQueryResponse = useAPI({
    queryKey: ['getRemitsByCms1500Id', by, id],
    queryFn: async () => {
      return await remitRepo.listRemitClaims({
        healthie_cms1500: data?.data?.id || '',
        person: data?.data?.patient || ''
      });
    },
    enabled: !!data
  });
  const { data: remitClaimData, error: remitError } = remitQueryResponse;

  if (by !== null && by !== undefined && !SUPPORTED_LOOKUPS.includes(by)) {
    return <Card>Invalid query parameter</Card>;
  }

  if (!data) {
    return <Card>Loading...</Card>;
  }

  if (error) {
    return <Card>Error: {error.message}</Card>;
  }

  const { data: cms1500 } = data;

  if (!cms1500) {
    return <Card>Could not find this claim</Card>;
  }

  const {
    first_sent_to_clearinghouse_at,
    last_sent_to_clearinghouse_at,
    primary_plan_name,
    rendering_provider_name_with_title,
    additional_text
  } = cms1500;

  const matchServiceLines = (serviceLine: Cms1500Cptcode, remitLines: RemitServiceLine[]) => {
    return remitLines.filter((remitLine) =>
      remitLine.claim_rows?.find((claimRow) => claimRow.cms1500_cpt_code_id === serviceLine.id)
    );
  };

  const sortedRemits =
    remitClaimData?.data?.results?.sort(
      (a, b) =>
        new Date(b.financial_info?.paid_at || 0).getTime() -
        new Date(a.financial_info?.paid_at || 0).getTime() +
        (a?.payer_classification === 'unspecified' ? 1 : -1)
    ) || [];

  const mostRecentRemitClaim = sortedRemits.reduce((acc, remit) => {
    if (!acc) {
      return remit;
    }

    if (!remit.financial_info?.paid_at) {
      return acc;
    }

    return new Date(remit.financial_info?.paid_at) < new Date(remit.financial_info?.paid_at)
      ? remit
      : acc;
  }, sortedRemits[0]);

  // Produce a hashmap of cms 1500 cpt codes line IDs to remit service lines, so we can easily match them up
  // and determine what remit service line is NOT matched to a service line for display separately
  const remitServiceLineMap = new Map<string, RemitServiceLine[]>();

  const hasCorrectedInformation = (remitClaim: RemitClaim) => {
    return (
      remitClaim.corrected_insured_first_name ||
      remitClaim.corrected_insured_last_name ||
      remitClaim.corrected_insured_middle_name ||
      remitClaim.corrected_insured_prefix ||
      remitClaim.corrected_insured_suffix ||
      remitClaim.corrected_insured_id_qualifier ||
      remitClaim.corrected_insured_id
    );
  };

  cms1500.cms1500cptcodes?.forEach((serviceLine) => {
    if (remitClaimData?.data?.results) {
      remitClaimData.data.results.forEach((remitClaim: RemitClaim) => {
        remitServiceLineMap.set(
          serviceLine.id,
          matchServiceLines(serviceLine, [
            ...remitClaim.service_lines,
            ...(remitServiceLineMap.get(serviceLine.id) || [])
          ])
        );
      });
    }
  });

  // Find all remit service lines that are not matched to a service line
  const unmatchedRemitServiceLines =
    remitClaimData?.data?.results
      .map((remitClaim) => remitClaim.service_lines)
      .flat()
      .filter(
        (remitServiceLine) =>
          remitServiceLine.claim_rows?.length === 0 ||
          !Array.from(remitServiceLineMap.values()).flat().includes(remitServiceLine)
      ) || [];

  const remitForRemitServiceLine = (remitServiceLine: RemitServiceLine) => {
    return remitClaimData?.data?.results?.find((remitClaim) =>
      remitClaim.service_lines.find((serviceLine) => serviceLine.id === remitServiceLine.id)
    );
  };

  const messages: string[] = [];

  if (
    mostRecentRemitClaim?.financial_info?.paid_at &&
    last_sent_to_clearinghouse_at &&
    new Date(last_sent_to_clearinghouse_at) >
      new Date(mostRecentRemitClaim?.financial_info?.paid_at)
  ) {
    messages.push(
      `A claim was sent to the clearinghouse after the most recent remit was received.  Sent: ${timeago.format(new Date(last_sent_to_clearinghouse_at))}`
    );
  }

  // Check the sum of the most recent patient responsibility amounts compared to adjustments with PR
  const totalPatientResponsibility = mostRecentRemitClaim?.service_lines.reduce(
    (acc, serviceLine) =>
      acc +
      ((serviceLine?.adjustments || [])
        .filter((adj) => adj?.group_code === 'PR')
        .reduce((acc, adj) => acc + (adj?.amount_in_cents || 0), 0) || 0),
    0
  );

  if (
    totalPatientResponsibility &&
    totalPatientResponsibility !== mostRecentRemitClaim?.patient_responsibility_amount_in_cents
  ) {
    messages.push(
      `The sum of the patient responsibility adjustments does not match the patient responsibility amount on the remit.`
    );
  }

  const { status, lastRemitChargeAmount, lastRemitPaidAmount, lastRemitPatientResponsibility } =
    computeClaimStatus(mostRecentRemitClaim, cms1500, true);

  const statusColorMap: { [key: string]: Color } = {
    'pending response from payer': Color.AMBER,
    'denied by insurance': Color.RED,
    'patient responsibility': Color.ORANGE,
    'partially paid': Color.YELLOW,
    'fully paid': Color.GREEN,
    'over payment': Color.BLUE,
    unknown: Color.GRAY
  };

  const statusColor = statusColorMap[status as keyof typeof statusColorMap] || Color.GRAY;

  const navigateToBillingItem = (billingItem: BillingItemMinimal) => {
    // Encounter Typ here is like appointments.appointmentattendance but needs to be translated to
    // appointment_attendance for routing.

    if (!billingItem.encounter_type) {
      return;
    }
    let encounter_type = billingItem.encounter_type;
    if (billingItem.encounter_type.includes('.')) {
      encounter_type = billingItem.encounter_type.split('.')[1];
    }
    const type = encounterTypeFromMapping(encounter_type);

    if (!type) {
      return;
    }

    navigateToEncounterWithoutPerson(type?.path, billingItem.encounter_id);
  };

  return (
    <Container type="page">
      <Card className="flex flex-col items-start">
        <Link to="/billing" className="text-blue-600">
          Billing Dashboard
        </Link>

        <ClaimLookup />
      </Card>
      <Card>
        <PageHeader
          headingText={
            <>
              Claim{' '}
              {!!cms1500.healthie_id && (
                <a
                  href={`${healthieLink({ host })}/cms1500s/${cms1500.healthie_id}/edit`}
                  className="my-2 text-blue-600"
                  target="_blank"
                  rel="noreferrer">
                  {cms1500.healthie_id}
                  <FontAwesomeIcon className="ml-1" icon={faExternalLink} />
                </a>
              )}
            </>
          }
        />
        {messages.length > 0 &&
          messages.map((alert) => (
            <Alert level="warn" key={alert}>
              {alert}
            </Alert>
          ))}
        <div className="my-2  divide-x-2 border-b-2">
          {!!cms1500?.healthie_id && (
            <>
              <a
                href={`${healthieLink({ host })}/cms1500s/${cms1500.healthie_id}/edit`}
                className="my-2 pr-2 text-blue-600"
                target="_blank"
                rel="noreferrer">
                View In Healthie
                <FontAwesomeIcon className="ml-1" icon={faExternalLink} />
              </a>
            </>
          )}

          {(cms1500.candid_encounter_ids || []).map((id) => (
            <ViewInCandid key={id} url={candidClaimUrl(host, id)} className="px-2" />
          ))}
        </div>
        <DataList key={cms1500.id}>
          <DataRow
            label="Patient Name"
            content={
              <Link
                className="mp-sensitive text-blue-700 underline"
                to={`/people/${cms1500.patient}`}>
                {cms1500.patient_full_name}
              </Link>
            }
          />
          <DataRow label="Date of Service" content={cms1500.date_of_service} />
          <DataRow label="Provider" content={cms1500.rendering_provider_name_with_title} />
          <DataRow
            label="Status"
            content={
              <>
                <Badge color={statusColor}>{status}</Badge>
                {status.toLowerCase() !== cms1500.status.toLowerCase() ? (
                  <>
                    Healthie Status: <Badge color={statusColor}>{cms1500.status}</Badge>
                  </>
                ) : null}
              </>
            }
          />

          <DataRow
            label="Created At"
            content={<DualTime dateTime={new Date(cms1500.created_at)} />}
          />

          {first_sent_to_clearinghouse_at && (
            <DataRow
              label="First sent to clearing house"
              content={
                new Date(first_sent_to_clearinghouse_at).toLocaleString() +
                ' ' +
                timeago.format(new Date(first_sent_to_clearinghouse_at))
              }
            />
          )}
          {last_sent_to_clearinghouse_at && (
            <DataRow
              label="Last sent to clearing house"
              content={
                new Date(last_sent_to_clearinghouse_at).toLocaleString() +
                ' ' +
                timeago.format(new Date(last_sent_to_clearinghouse_at))
              }
            />
          )}
          {primary_plan_name && <DataRow label="Primary Plan Name" content={primary_plan_name} />}
          {rendering_provider_name_with_title && (
            <DataRow label="Rendering Provider" content={rendering_provider_name_with_title} />
          )}
          {additional_text && (
            <DataRow label="Optional Note" content={<SimpleFormat text={additional_text} />} />
          )}
        </DataList>
        <Heading value={3} text="Policies" />
        {cms1500.policies?.map((policy) => {
          return <InsuranceSummary key={policy.id} insurancePolicy={policy} />;
        })}

        <Heading value={3} text="Most Recent Remit" />
        <DataList>
          <DataRow label="Charged">{currencyInCentsFormatter(lastRemitChargeAmount)}</DataRow>
          <DataRow label="Paid">
            {lastRemitPaidAmount && currencyInCentsFormatter(lastRemitPaidAmount)}
          </DataRow>
          <DataRow label="Patient Responsibility">
            {currencyInCentsFormatter(lastRemitPatientResponsibility)}
          </DataRow>
          <DataRow label="Claim Coverage">
            {currencyInCentsFormatter(mostRecentRemitClaim?.claim_coverage_amount_in_cents || 0)}
          </DataRow>
        </DataList>

        <Heading value={2} text={`Service Lines (${cms1500.cms1500cptcodes?.length})`} />

        {cms1500.cms1500cptcodes?.map((claim_row) => {
          const mods = claim_row
            ? [claim_row.mod1, claim_row.mod2, claim_row.mod3, claim_row.mod4]
            : [];
          const shouldDisplayMods = mods.some((mod) => mod !== '');

          return (
            <Card key={claim_row.id} className="mt-4 border-t pt-2 border-y-gray-200 border-solid">
              {claim_row &&
                claim_row.billing_items.map((billingItem) => {
                  return (
                    <button
                      className="ml-2 active:bg-gray-200 active:text-gray-800 drop-shadow-sm"
                      key={billingItem.id}
                      onClick={() => navigateToBillingItem(billingItem)}
                      type="button">
                      <Badge color={Color.GREEN}>
                        <>
                          <FontAwesomeIcon icon={faFileMedical} className="mr-1" />
                          View Encounter
                        </>
                      </Badge>
                    </button>
                  );
                })}
              <DataList>
                {claim_row && (
                  <DataRow
                    label="Code"
                    content={
                      <Tooltip tooltipContent={claim_row.cpt_code_description}>
                        {claim_row.cpt_code_code} <FontAwesomeIcon icon={faQuestionCircle} />
                      </Tooltip>
                    }
                  />
                )}
                {claim_row && <DataRow label="Service Date" content={claim_row.service_date} />}
                {shouldDisplayMods && (
                  <DataRow
                    label="Modifiers"
                    content={
                      <>
                        {mods
                          .filter((mod) => mod)
                          .map((mod) => {
                            return (
                              <span key={`mod-${mod}`} className="mr-1">
                                <Tooltip
                                  tooltipContent={
                                    mod
                                      ? ModifierRegistry[mod as keyof typeof ModifierRegistry]
                                          ?.description
                                      : mod
                                  }>
                                  {mod}
                                </Tooltip>
                              </span>
                            );
                          })}
                        <FontAwesomeIcon icon={faCircleQuestion} />
                      </>
                    }
                  />
                )}
                {claim_row?.fee_in_cents !== undefined && (
                  <DataRow
                    label="Fee"
                    content={`${currencyInCentsFormatter(claim_row.fee_in_cents || 0)} x ${
                      claim_row.units
                    }`}
                  />
                )}
              </DataList>
              {(remitServiceLineMap.get(claim_row.id) || []).length > 0 && (
                <Heading value={3} text="Remits" />
              )}
              {unmatchedRemitServiceLines.length ? (
                <Alert level="warn">
                  There are remits that do not match this service line. Please review the remits for
                  this claim. Linked remits are displayed below.
                  <a href="#unmatched-remit-service-lines">Jump to unmatched remit service lines</a>
                </Alert>
              ) : null}
              {(remitServiceLineMap.get(claim_row.id) || [])?.reverse().map((remitLine) => {
                const remitClaim = remitForRemitServiceLine(remitLine);
                const remitClaimStatus =
                  ClaimStatusRegistry[remitClaim?.status as keyof typeof ClaimStatusRegistry];
                return (
                  <div key={remitLine.id} className="pt-2">
                    <strong className="mr-2 ">
                      {remitClaim?.financial_info?.paid_at &&
                        new Date(remitClaim?.financial_info?.paid_at).toDateString()}
                    </strong>
                    {remitClaimStatus && (
                      <Badge
                        color={remitClaimStatus.level == 'alert' ? Color.RED : Color.BLUE}
                        className="mr-2">
                        {remitClaimStatus.description}
                      </Badge>
                    )}
                    by <strong>{remitClaim?.financial_info?.payer_name}</strong>
                    <Badge
                      className="mr-2"
                      color={
                        statusColorMap[
                          computeClaimStatus(
                            remitClaim,
                            cms1500,
                            remitClaim === mostRecentRemitClaim
                          )?.status as keyof typeof statusColorMap
                        ] || Color.GRAY
                      }>
                      {computeClaimStatus(remitClaim, cms1500)?.status}
                    </Badge>
                    <Badge color={Color.GRAY} className="mr-2">
                      {String(remitClaim?.payer_classification)}
                    </Badge>
                    <Tooltip tooltipContent="Payer Claim Control Number (ICN)">
                      <CopyContent
                        content={`${remitClaim?.payer_claim_control_number}`}
                        copyButtonClassName="inline"
                        wrapperClassName="truncate ellipsis block-inline max-w-xs"
                      />
                    </Tooltip>
                    {remitClaim?.remit_id && (
                      <Link
                        to={`/billing/remit/${remitClaim?.remit_id}/edit?highlight_remit_claim_id=${remitClaim?.id}`}
                        className="text-blue-600 ml-2">
                        <FontAwesomeIcon icon={faExternalLink} />
                      </Link>
                    )}
                    {remitLine.claim_rows ? (
                      <Tooltip
                        tooltipContent={
                          <div>
                            {remitLine.claim_rows.map((claimRow) => {
                              return (
                                <div key={claimRow.id}>
                                  <strong></strong>
                                  <strong>Link Quality {claimRow.link_score}</strong>{' '}
                                  {Object.entries(claimRow.link_attributes).map(([key, value]) => {
                                    return (
                                      <div key={key}>
                                        <strong>{titleize(key)}</strong>:{' '}
                                        {value === 1 ? <FontAwesomeIcon icon={faCheck} /> : value}
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })}
                          </div>
                        }>
                        <FontAwesomeIcon icon={faChain} className="ml-2" />
                      </Tooltip>
                    ) : null}
                    <RemitServiceLineCard serviceLine={remitLine} editable={false} />
                  </div>
                );
              })}
            </Card>
          );
        })}

        {unmatchedRemitServiceLines.length > 0 && (
          <Card className="mt-4 border-t pt-2 border-y-gray-200 border-solid">
            <Heading value={3} text="Unmatched Remit Service Lines" />

            {unmatchedRemitServiceLines.map((remitLine) => {
              return (
                <div key={remitLine.id} className="pt-2">
                  <RemitServiceLineCard serviceLine={remitLine} editable={false} />
                </div>
              );
            })}
          </Card>
        )}

        <Heading value={2} text={`Linked Remits (${sortedRemits.length})`} />

        {remitError && <Card>Error: {remitError.message}</Card>}

        <div className="mt-4 border-t p-4 border-y-gray-200 border-solid">
          {sortedRemits.length > 0 && (
            <table className="table-auto w-full">
              <thead className="text-left">
                <tr>
                  <th>Remit</th>
                  <th>Payer</th>
                  <th>Subscriber ID</th>
                  <th>
                    <Tooltip tooltipContent={'Payer Claim Control Number'}>ICN</Tooltip>
                  </th>
                  <th>EFT / Check</th>
                  <th>Paid At</th>
                  <th>Lines</th>
                  <th>Paid</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sortedRemits.map((remitClaim: RemitClaim) => {
                  const remitClaimStatus =
                    ClaimStatusRegistry[remitClaim?.status as keyof typeof ClaimStatusRegistry];

                  return (
                    <tr key={`remit-${remitClaim.id}`} className="border-b-2 border-solid my-2">
                      <td className="max-w-15">
                        <Link
                          to={`/billing/remit/${remitClaim.remit_id}/edit?highlight_remit_claim_id=${remitClaim.id}`}
                          className="text-blue-600 whitespace-nowrap">
                          {remitClaim.remit_id}
                          <Tooltip
                            tooltipContent={
                              remitClaim.remit?.type == 'paper'
                                ? 'Paper'
                                : `Electronic (${remitClaim.remit?.ingested_from})`
                            }>
                            {remitClaim.remit?.type == 'paper' ? (
                              <FontAwesomeIcon icon={faFile} className="pl-2" />
                            ) : remitClaim.remit?.ingested_from == 'candid' ? (
                              <CandidLogo size={16} />
                            ) : (
                              <FontAwesomeIcon icon={faBoltLightning} className="pl-2" />
                            )}
                          </Tooltip>
                        </Link>
                      </td>
                      <td>{remitClaim?.financial_info?.payer_name}</td>

                      <td>
                        <CopyContent
                          content={`${remitClaim?.corrected_insured_id || remitClaim.patient_id}`}
                          copyButtonClassName="inline"
                          wrapperClassName="truncate ellipsis block-inline max-w-xs"
                        />
                      </td>
                      <td>
                        <CopyContent
                          content={`${remitClaim?.payer_claim_control_number}`}
                          copyButtonClassName="inline"
                          wrapperClassName="truncate ellipsis block-inline max-w-xs"
                        />
                      </td>
                      <td>
                        <CopyContent
                          content={`${remitClaim?.financial_info?.check_or_eft_trace_number}`}
                          copyButtonClassName="inline"
                          wrapperClassName="truncate ellipsis block-inline max-w-xs"
                        />
                      </td>
                      <td>
                        {remitClaim?.financial_info?.paid_at &&
                          new Date(remitClaim?.financial_info?.paid_at).toDateString()}
                      </td>
                      <td>{remitClaim.service_lines.length}</td>
                      <td>{currencyInCentsFormatter(remitClaim.paid_amount_in_cents || 0)}</td>
                      <td>
                        {remitClaimStatus && (
                          <Badge
                            color={remitClaimStatus.level == 'alert' ? Color.RED : Color.BLUE}
                            className="mr-2 inline-block">
                            {remitClaimStatus.description}
                          </Badge>
                        )}

                        <Badge
                          className="mr-2 inline-block"
                          color={
                            statusColorMap[
                              computeClaimStatus(remitClaim, cms1500)
                                ?.status as keyof typeof statusColorMap
                            ] || Color.GRAY
                          }>
                          {computeClaimStatus(remitClaim, cms1500)?.status}
                        </Badge>

                        <Badge color={Color.GRAY} className="mr-2 inline-block">
                          {String(remitClaim?.payer_classification)}
                        </Badge>

                        {hasCorrectedInformation(remitClaim) && (
                          <Tooltip
                            tooltipContent={
                              <div>
                                {remitClaim.corrected_insured_first_name && (
                                  <div>
                                    Corrected Insured First Name:{' '}
                                    {remitClaim.corrected_insured_first_name}
                                  </div>
                                )}
                                {remitClaim.corrected_insured_middle_name && (
                                  <div>
                                    Corrected Insured Middle Name:{' '}
                                    {remitClaim.corrected_insured_middle_name}
                                  </div>
                                )}
                                {remitClaim.corrected_insured_last_name && (
                                  <div>
                                    Corrected Insured Last Name:{' '}
                                    {remitClaim.corrected_insured_last_name}
                                  </div>
                                )}
                                {remitClaim.corrected_insured_prefix && (
                                  <div>
                                    Corrected Insured Prefix: {remitClaim.corrected_insured_prefix}
                                  </div>
                                )}
                                {remitClaim.corrected_insured_suffix && (
                                  <div>
                                    Corrected Insured Suffix: {remitClaim.corrected_insured_suffix}
                                  </div>
                                )}
                                {remitClaim.corrected_insured_id_qualifier && (
                                  <div>
                                    Corrected Insured ID Qualifier:{' '}
                                    {remitClaim.corrected_insured_id_qualifier}
                                  </div>
                                )}
                                {remitClaim.corrected_insured_id && (
                                  <div>Corrected Insured ID: {remitClaim.corrected_insured_id}</div>
                                )}
                              </div>
                            }>
                            <span>
                              <FontAwesomeIcon icon={faCircleInfo} className="mr-2" />
                              Has Payer Corrected Information
                            </span>
                          </Tooltip>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {sortedRemits.length === 0 && (
            <div className="text-gray-800 text-center">No remits found for this claim</div>
          )}
          <Debug object={cms1500} />
        </div>
      </Card>
      <NoteList object_id={cms1500.id} object_name={`this claim`} contentTypeName="cms1500" />
      <TaskList
        personId={cms1500.patient}
        object_id={cms1500.id}
        object_name={`this encounter`}
        contentTypeName="healthie.cms1500"
      />
    </Container>
  );
};

export default BillingClaim;
